import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    styled,
    useTheme,
    alpha,
    CircularProgress,
    IconButton
} from '@mui/material';
import { Box } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import Text from 'components/mui/Text';
import AppButtonComponent from 'components/mui/AppButtonComponent';
import { useCreateAgreementSignRequestMutation } from 'store/api/agreement.apislice';
import { useGetOnboardingStatusQuery } from 'store/api/customer.apislice';
import toast from 'react-hot-toast';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        background: "#13172A",
        borderRadius: "16px",
        color: "#fff",
        maxWidth: 420,
        width: "100%",
        margin: theme.spacing(2),
        maxHeight: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
    },
    "& .MuiBackdrop-root": {
        backgroundColor: "rgba(0, 0, 0, 0.65)",
    },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3, 4),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    "&:first-of-type": { paddingTop: theme.spacing(3) },
}));

const DigioAgreementModal = ({ open = false, onClose, userName }) => {
    const theme = useTheme();
    const digioRef = useRef(null);
    const unmountedRef = useRef(false);

    // Flow phases: 'loading' | 'ready' | 'signing' | 'verifying' | 'error'
    const [phase, setPhase] = useState('loading');
    const [signRequestData, setSignRequestData] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');

    const [createSignRequest] = useCreateAgreementSignRequestMutation();
    const { refetch: refetchOnboarding } = useGetOnboardingStatusQuery(undefined, { skip: true });

    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            if (digioRef.current) {
                try {
                    digioRef.current.cancel();
                } catch (e) {
                    console.error(e);
                }
            }
        };
    }, []);

    // Load sign request data when dialog opens
    useEffect(() => {
        if (open) {
            loadSignRequest();
        } else {
            // Reset state on close
            setPhase('loading');
            setSignRequestData(null);
            setErrorMsg('');
        }
    }, [open]);

    const loadSignRequest = async () => {
        setPhase('loading');
        setErrorMsg('');
        try {
            const response = await createSignRequest().unwrap();
            if (response.success && response.documentId && response.tokenId) {
                if (!unmountedRef.current) {
                    setSignRequestData(response);
                    setPhase('ready');
                }
            } else {
                throw new Error(response.message || 'Failed to initiate signing request.');
            }
        } catch (err) {
            console.error('Agreement signature init failed:', err);
            if (!unmountedRef.current) {
                setErrorMsg(err?.data?.message || err?.message || 'Failed to prepare the agreement document.');
                setPhase('error');
            }
        }
    };

    const handleStartDigio = () => {
        if (!signRequestData) return;
        const { documentId, tokenId, identifier } = signRequestData;

        try {
            setPhase('signing');
            digioRef.current = new window.Digio({
                environment: 'production', // matches WelcometoMarkdown PRODUCTION setting
                logo: "https://www.inxits.com/favicon.ico",
                is_iframe: true,
                is_redirection_approach: false,
                theme: {
                    primaryColor: "#46A8DC",
                    secondaryColor: "#0D0F1D",
                },
                callback: async (response) => {
                    console.log('Digio agreement callback:', response);
                    if (!response.error_code) {
                        if (!unmountedRef.current) {
                            setPhase('verifying');
                            pollAgreementStatus();
                        }
                    } else if (response.error_code === "CANCELLED") {
                        toast.error("Signature process was cancelled.");
                        if (!unmountedRef.current) {
                            setPhase('ready');
                        }
                    } else {
                        toast.error(response.message || "Digio e-sign process failed.");
                        if (!unmountedRef.current) {
                            setPhase('error');
                            setErrorMsg(response.message || "E-sign process failed.");
                        }
                    }
                }
            });

            digioRef.current.init();
            digioRef.current.submit(documentId, identifier, tokenId);
        } catch (error) {
            console.error("Failed to load Digio SDK client:", error);
            toast.error("Failed to launch Digio signature window.");
            setPhase('error');
            setErrorMsg("Could not load signature overlay.");
        }
    };

    const pollAgreementStatus = async () => {
        const POLL_INTERVAL_MS = 2000;
        const POLL_MAX_ATTEMPTS = 15; // 30 seconds window
        let attempt = 0;

        const checkStatus = async () => {
            if (unmountedRef.current) return true;
            attempt++;
            try {
                const res = await refetchOnboarding().unwrap();
                const completed = res?.data?.agreement?.completed || res?.agreement?.completed;
                if (completed) {
                    toast.success("Agreement signed and verified successfully!");
                    if (onClose) onClose();
                    return true;
                }
            } catch (err) {
                console.error("Error polling agreement onboarding status:", err);
            }

            if (attempt >= POLL_MAX_ATTEMPTS) {
                toast.error("Signature verification took longer than expected. It will verify in the background.");
                if (onClose) onClose();
                return true;
            }
            return false;
        };

        const firstCheck = await checkStatus();
        if (firstCheck) return;

        const intervalId = setInterval(async () => {
            const finished = await checkStatus();
            if (finished) {
                clearInterval(intervalId);
            }
        }, POLL_INTERVAL_MS);
    };

    return (
        <StyledDialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onClose();
                }
            }}
            disableEscapeKeyDown
            maxWidth="xs"
        >
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <StyledDialogContent>
                {phase === 'loading' && (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                        <Text sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                            Preparing your investment agreement...
                        </Text>
                    </Box>
                )}

                {phase === 'ready' && (
                    <Box sx={{ width: '100%' }}>
                        <Text sx={{ fontSize: '20px', fontWeight: 700, color: '#fff', mb: 1.5 }}>
                            Signature Required
                        </Text>
                        <Text sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.5, mb: 3 }}>
                            Welcome, {userName || "Investor"}! To activate your <b>Direct Plan</b> and start investing, please sign the SEBI-mandated Investment Advisory Agreement via Digio E-Sign.
                        </Text>

                        <AppButtonComponent
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleStartDigio}
                            sx={{
                                py: 1.5,
                                borderRadius: "10px",
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "none",
                            }}
                        >
                            Proceed to E-Sign
                        </AppButtonComponent>
                    </Box>
                )}

                {phase === 'signing' && (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                        <Text sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                            Opening E-Sign window...
                        </Text>
                        <Text sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                            Please sign using your Aadhaar or OTP credentials in the popup.
                        </Text>
                    </Box>
                )}

                {phase === 'verifying' && (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: theme.palette.success.main }} />
                        <Text sx={{ fontSize: '15px', color: '#fff', fontWeight: 600 }}>
                            Verifying signature status...
                        </Text>
                        <Text sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            We are confirming your signature details with Digio. Please do not close the app.
                        </Text>
                    </Box>
                )}

                {phase === 'error' && (
                    <Box sx={{ width: '100%' }}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }} />
                        <Text sx={{ fontSize: '18px', fontWeight: 700, color: '#fff', mb: 1 }}>
                            Preparation Failed
                        </Text>
                        <Text sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.5, mb: 3 }}>
                            {errorMsg || "An error occurred while preparing your signing request. Please try again."}
                        </Text>

                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <AppButtonComponent
                                fullWidth
                                variant="outlined"
                                color="inherit"
                                onClick={loadSignRequest}
                                sx={{
                                    py: 1.25,
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    textTransform: "none",
                                    color: '#fff',
                                    borderColor: 'rgba(255,255,255,0.2)',
                                }}
                            >
                                Retry
                            </AppButtonComponent>
                        </Box>
                    </Box>
                )}
            </StyledDialogContent>
        </StyledDialog>
    );
};

export default DigioAgreementModal;
